import { useEffect, useState } from 'react';
import { supabase, Agency, Application } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  Copy, Check, ExternalLink, Users, FileQuestion, TrendingUp, Clock, Share2,
  Facebook, Linkedin, Mail, QrCode, Link2,
} from 'lucide-react';

const APP_BASE = window.location.origin;

export default function DashboardHome() {
  const { agency } = useAuth();
  const [copied, setCopied] = useState(false);
  const [stats, setStats] = useState({ total: 0, new: 0, interview: 0, hired: 0 });
  const [recentApps, setRecentApps] = useState<Application[]>([]);

  useEffect(() => {
    if (agency) loadStats();
  }, [agency]);

  async function loadStats() {
    const { data } = await supabase
      .from('applications')
      .select('status, submitted_at')
      .eq('agency_id', agency!.id);

    const apps = data ?? [];
    setStats({
      total: apps.length,
      new: apps.filter((a) => a.status === 'new').length,
      interview: apps.filter((a) => a.status === 'interview').length,
      hired: apps.filter((a) => a.status === 'hired').length,
    });

    const { data: recent } = await supabase
      .from('applications')
      .select('*')
      .eq('agency_id', agency!.id)
      .order('submitted_at', { ascending: false })
      .limit(5);
    setRecentApps(recent ?? []);
  }

  const applyLink = `${APP_BASE}/apply/${agency?.slug}`;

  function copyLink() {
    navigator.clipboard.writeText(applyLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  }

  const STATUS_COLORS: Record<string, string> = {
    new: 'bg-blue-100 text-blue-700',
    reviewing: 'bg-yellow-100 text-yellow-700',
    interview: 'bg-purple-100 text-purple-700',
    hired: 'bg-green-100 text-green-700',
    rejected: 'bg-red-100 text-red-600',
  };

  const shareChannels = [
    {
      icon: Facebook,
      label: 'Facebook',
      color: 'text-blue-600 bg-blue-50 hover:bg-blue-100 border-blue-100',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(applyLink)}`,
    },
    {
      icon: Linkedin,
      label: 'LinkedIn',
      color: 'text-blue-700 bg-blue-50 hover:bg-blue-100 border-blue-100',
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(applyLink)}`,
    },
    {
      icon: Mail,
      label: 'Email',
      color: 'text-gray-700 bg-gray-50 hover:bg-gray-100 border-gray-100',
      href: `mailto:?subject=Join ${agency?.name} — Apply Now&body=Apply here: ${applyLink}`,
    },
  ];

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900">Dashboard</h2>
        <p className="text-sm text-gray-500 mt-0.5">Welcome back, {agency?.name}</p>
      </div>

      {/* Application link */}
      <div className="bg-blue-700 rounded-lg p-5 mb-6 text-white">
        <div className="flex items-center gap-2 mb-3">
          <Link2 className="w-4 h-4 text-blue-200" />
          <p className="text-sm font-semibold text-blue-100">Your Application Link</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex-1 bg-blue-500/40 rounded-md px-4 py-3 font-mono text-sm truncate">
            {applyLink}
          </div>
          <button
            onClick={copyLink}
            className={`flex items-center gap-1.5 px-4 py-3 rounded-md font-semibold text-sm transition-colors flex-shrink-0 ${
              copied ? 'bg-green-500 text-white' : 'bg-white text-blue-700 hover:bg-blue-50'
            }`}
          >
            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <a
            href={applyLink}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1.5 px-4 py-3 bg-blue-500/40 hover:bg-blue-500/60 rounded-md text-sm font-medium transition-colors flex-shrink-0"
          >
            <ExternalLink className="w-4 h-4" />
            Preview
          </a>
        </div>

        {/* Share buttons */}
        <div className="flex items-center gap-2 mt-4 pt-4 border-t border-blue-500/40">
          <Share2 className="w-3.5 h-3.5 text-blue-300" />
          <span className="text-xs text-blue-200 font-medium mr-2">Share on:</span>
          {shareChannels.map(({ icon: Icon, label, href }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-1.5 bg-white/15 hover:bg-white/25 rounded-lg text-xs font-medium text-white transition-colors"
            >
              <Icon className="w-3 h-3" />
              {label}
            </a>
          ))}
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          { label: 'Total Applications', value: stats.total, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'New (unread)', value: stats.new, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Interviewing', value: stats.interview, icon: TrendingUp, color: 'text-purple-600', bg: 'bg-purple-50' },
          { label: 'Hired', value: stats.hired, icon: Check, color: 'text-green-600', bg: 'bg-green-50' },
        ].map(({ label, value, icon: Icon, color, bg }) => (
          <div key={label} className="bg-white border border-gray-200 rounded-lg p-5">
            <div className={`w-10 h-10 rounded-md ${bg} ${color} flex items-center justify-center mb-3`}>
              <Icon className="w-5 h-5" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{label}</p>
          </div>
        ))}
      </div>

      {/* Recent applications */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <h3 className="font-bold text-gray-900 text-sm">Recent Applications</h3>
          <span className="text-xs text-gray-400">Last 5</span>
        </div>
        {recentApps.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-7 h-7 text-gray-300 mx-auto mb-2" />
            <p className="text-gray-400 text-sm">No applications yet. Share your link to get started.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {recentApps.map((app) => (
              <div key={app.id} className="flex items-center gap-4 px-5 py-3.5">
                <div className="w-8 h-8 rounded-md bg-blue-50 flex items-center justify-center text-blue-600 font-bold text-xs flex-shrink-0">
                  {app.candidate_name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{app.candidate_name}</p>
                  <p className="text-xs text-gray-400">{app.candidate_email}</p>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_COLORS[app.status]}`}>
                  {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                </span>
                <span className="text-xs text-gray-400 flex-shrink-0 hidden sm:block">
                  {new Date(app.submitted_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
